/**
 * Dynamic Filter Configuration Hook
 *
 * Provides backend-driven filter configurations for UI components,
 * eliminating hardcoded filter values and enabling metadata-driven customization.
 *
 * Features:
 * - Tenant-specific filter configurations
 * - Component-specific customization
 * - Real-time configuration updates
 * - Fallback to default values during loading
 */

import { useQuery } from '@apollo/client/react';
import {
    GET_AVAILABLE_COMPLIANCE_FRAMEWORKS,
    GET_BUSINESS_PROCESS_TYPES,
    GET_DEPARTMENT_CONFIGURATION,
    GET_FILTER_CONFIGURATIONS,
    GET_REGULATORY_AUTHORITY_TYPES
} from '@ferp/graphql-client';
import { useMemo } from 'react';

// Types for dynamic filter configuration
interface FilterConfiguration {
  fieldName: string;
  filterType: 'list' | 'input' | 'date' | 'number';
  availableValues: string[];
  defaultValues?: string[];
  isSearchable: boolean;
  isMultiSelect: boolean;
  category: string;
  priority: number;
}

interface ComplianceFramework {
  frameworkId: string;
  name: string;
  description: string;
  category: string;
  regulatoryAuthority: string;
  applicableDomains: string[];
  severityLevel: string;
}

interface Department {
  id: string;
  name: string;
  description: string;
  parentDepartment?: string;
  isActive: boolean;
}

interface RegulatoryAuthorityType {
  authorityType: string;
  name: string;
  description: string;
  jurisdictionLevel: string;
  applicableIndustries: string[];
}

interface BusinessProcessType {
  processType: string;
  name: string;
  description: string;
  category: string;
  typicalDuration?: string;
  complexityLevel: string;
}

// Default fallback values (used during loading)
const DEFAULT_VALUES = {
  status: ['pending', 'in_progress', 'completed', 'failed', 'stuck'],
  priority: ['low', 'medium', 'high', 'critical'],
  riskLevel: ['low', 'medium', 'high'],
  complianceLevel: ['FULLY_COMPLIANT', 'SUBSTANTIALLY_COMPLIANT', 'PARTIALLY_COMPLIANT', 'NON_COMPLIANT', 'UNDER_REVIEW', 'EXEMPTED'],
  riskClassification: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'CATASTROPHIC'],
  certificationLevel: ['BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTER'],
  customerType: ['B2B', 'B2C', 'Internal', 'Partner'],
  criticality: ['low', 'medium', 'high', 'critical']
};

/**
 * Main hook for dynamic filter configuration
 */
export const useDynamicFilterConfig = (tenant: string, component: string) => {
  // Load component-specific filter configurations
  const {
    data: filterConfigData,
    loading: filterConfigLoading,
    error: filterConfigError
  } = useQuery<Record<string, any>>(GET_FILTER_CONFIGURATIONS, {
    variables: { tenant, component },
    errorPolicy: 'all'
  });

  // Load compliance frameworks
  const {
    data: complianceData,
    loading: complianceLoading
  } = useQuery<Record<string, any>>(GET_AVAILABLE_COMPLIANCE_FRAMEWORKS, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  // Load department configuration
  const {
    data: departmentData,
    loading: departmentLoading
  } = useQuery<Record<string, any>>(GET_DEPARTMENT_CONFIGURATION, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  // Load regulatory authority types
  const {
    data: authorityData,
    loading: authorityLoading
  } = useQuery<Record<string, any>>(GET_REGULATORY_AUTHORITY_TYPES, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  // Load business process types
  const {
    data: processTypeData,
    loading: processTypeLoading
  } = useQuery<Record<string, any>>(GET_BUSINESS_PROCESS_TYPES, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  // Memoized filter configurations
  const filterConfigs = useMemo(() => {
    const configs: Record<string, string[]> = {};

    // Apply component-specific configurations
    if (filterConfigData?.getFilterConfigurations) {
      filterConfigData.getFilterConfigurations.forEach((config: FilterConfiguration) => {
        configs[config.fieldName] = config.availableValues;
      });
    }

    // Apply compliance frameworks
    if (complianceData?.getAvailableComplianceFrameworks) {
      configs.compliance = complianceData.getAvailableComplianceFrameworks.map(
        (framework: ComplianceFramework) => framework.frameworkId
      );
    }

    // Apply department configuration
    if (departmentData?.getDepartmentConfiguration?.departments) {
      configs.department = departmentData.getDepartmentConfiguration.departments
        .filter((dept: Department) => dept.isActive)
        .map((dept: Department) => dept.name);
    }

    // Apply regulatory authority types
    if (authorityData?.getRegulatoryAuthorityTypes) {
      configs.authorityType = authorityData.getRegulatoryAuthorityTypes.map(
        (authority: RegulatoryAuthorityType) => authority.authorityType
      );
    }

    // Apply business process types
    if (processTypeData?.getBusinessProcessTypes) {
      configs.processType = processTypeData.getBusinessProcessTypes.map(
        (processType: BusinessProcessType) => processType.processType
      );

      // Create category values from process types
      configs.category = [...new Set(
        processTypeData.getBusinessProcessTypes.map(
          (processType: BusinessProcessType) => processType.category
        )
      )] as string[];
    }

    // Apply default values for fields not configured
    Object.entries(DEFAULT_VALUES).forEach(([field, values]) => {
      if (!configs[field]) {
        configs[field] = values;
      }
    });

    return configs;
  }, [
    filterConfigData,
    complianceData,
    departmentData,
    authorityData,
    processTypeData
  ]);

  // Loading state - true if any critical query is loading
  const loading = complianceLoading || departmentLoading || authorityLoading;

  // Error handling
  const error = filterConfigError;

  return {
    filterConfigs,
    loading,
    error,
    // Individual data for advanced usage
    complianceFrameworks: complianceData?.getAvailableComplianceFrameworks || [],
    departments: departmentData?.getDepartmentConfiguration?.departments || [],
    authorityTypes: authorityData?.getRegulatoryAuthorityTypes || [],
    processTypes: processTypeData?.getBusinessProcessTypes || []
  };
};

/**
 * Specialized hook for compliance frameworks only
 */
export const useComplianceFrameworks = (tenant: string) => {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_AVAILABLE_COMPLIANCE_FRAMEWORKS, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  return {
    frameworks: data?.getAvailableComplianceFrameworks || [],
    frameworkIds: data?.getAvailableComplianceFrameworks?.map((f: ComplianceFramework) => f.frameworkId) || [],
    loading,
    error
  };
};

/**
 * Specialized hook for department configuration
 */
export const useDepartmentConfiguration = (tenant: string) => {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_DEPARTMENT_CONFIGURATION, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  return {
    departments: data?.getDepartmentConfiguration?.departments || [],
    departmentNames: data?.getDepartmentConfiguration?.departments
      ?.filter((dept: Department) => dept.isActive)
      ?.map((dept: Department) => dept.name) || [],
    organizationalStructure: data?.getDepartmentConfiguration?.organizationalStructure,
    loading,
    error
  };
};